import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Ban, Lock, CalendarDays, Clock, Layers } from "lucide-react";
import { api, apiError, fmtHour } from "@/lib/api";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";

function fmtDate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
const sameScope = (a = [], b = []) => a.length === b.length && [...a].sort().every((x, i) => x === [...b].sort()[i]);

const ScopePicker = ({ services, scope, setScope }) => {
  const toggle = (id) => setScope(scope.includes(id) ? scope.filter((x) => x !== id) : [...scope, id]);
  const chip = (active) => `px-3 py-1.5 rounded-full text-xs transition-colors ${active ? "btn-gold" : "border border-white/15 text-white/60 hover:border-white/40"}`;
  return (
    <div className="glass rounded-2xl p-4 mb-6" data-testid="block-scope-picker">
      <p className="text-white/60 text-xs uppercase tracking-wide font-mono-label mb-3 flex items-center gap-2"><Layers size={13} className="text-[#D4AF37]" /> Blocks apply to</p>
      <div className="flex flex-wrap gap-2">
        <button onClick={() => setScope([])} className={chip(scope.length === 0)} data-testid="scope-all">All services</button>
        {services.map((s) => (
          <button key={s.id} onClick={() => toggle(s.id)} className={chip(scope.includes(s.id))} data-testid={`scope-${s.slug || s.id}`}>{s.title}</button>
        ))}
      </div>
      <p className="text-white/35 text-[11px] mt-3">
        {scope.length === 0 ? "New blocks close the time for every service." : `New blocks only hide the time for: ${services.filter((s) => scope.includes(s.id)).map((s) => s.title).join(", ")}.`}
      </p>
    </div>
  );
};

export default function Availability() {
  const qc = useQueryClient();
  const [reason, setReason] = useState("");
  const [selected, setSelected] = useState(null);
  const [scope, setScope] = useState([]);

  const { data: services = [] } = useQuery({ queryKey: ["services"], queryFn: () => api.get("/services").then((r) => r.data) });
  const { data } = useQuery({ queryKey: ["availability-admin"], queryFn: () => api.get("/availability", { params: { all: true } }).then((r) => r.data) });
  const nameOf = (ids = []) => (ids.length ? ids.map((id) => services.find((s) => s.id === id)?.title || "Removed service").join(", ") : "All services");
  const blocked = data?.blocked_dates || [];
  const fullDays = new Set(blocked.filter((b) => !(b.service_ids || []).length).map((b) => b.date));
  const partialDays = new Set(blocked.filter((b) => (b.service_ids || []).length).map((b) => b.date));
  const selDate = selected ? fmtDate(selected) : null;

  const { data: dayInfo } = useQuery({
    queryKey: ["avail-day-admin", selDate],
    queryFn: () => api.get("/availability/day", { params: { date: selDate } }).then((r) => r.data),
    enabled: !!selDate,
  });
  const refresh = () => {
    ["availability-admin", "avail-day-admin", "availability", "day"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
  };
  const run = async (fn, msg) => { try { await fn(); if (msg) toast.success(msg); refresh(); } catch (e) { toast.error(apiError(e)); } };

  const dayBlocks = dayInfo?.day_blocks || [];
  const scopeDayBlock = dayBlocks.find((b) => sameScope(b.service_ids, scope));
  const toggleDay = () => scopeDayBlock
    ? run(() => api.delete(`/availability/${selDate}`, { params: { block_id: scopeDayBlock.id } }), "Day unblocked")
    : run(() => api.post("/availability", { date: selDate, reason, service_ids: scope }), "Day blocked");

  const slotBlocks = dayInfo?.slot_blocks || [];
  const toggleSlot = (h) => {
    const mine = slotBlocks.find((b) => b.hour === h && sameScope(b.service_ids, scope));
    return mine
      ? run(() => api.delete(`/availability/slot/${selDate}/${h}`, { params: { block_id: mine.id } }))
      : run(() => api.post("/availability/slot", { date: selDate, hour: h, reason, service_ids: scope }));
  };

  const allDayBlocked = dayBlocks.some((b) => !(b.service_ids || []).length);
  const closedWeekday = dayInfo && !dayInfo.open && !allDayBlocked;
  const bookedHours = new Set(dayInfo?.booked_hours || []);
  const slots = dayInfo ? Array.from({ length: dayInfo.end - dayInfo.start }, (_, i) => dayInfo.start + i) : [];

  return (
    <div data-testid="admin-availability">
      <h1 className="font-display text-3xl text-white mb-1">Availability</h1>
      <p className="text-white/50 text-sm mb-6">Choose which services a block applies to, then pick a day to block it, or tap individual hourly slots.</p>
      <ScopePicker services={services} scope={scope} setScope={setScope} />

      <div className="grid md:grid-cols-2 gap-8">
        <div>
          <Input placeholder="Reason (optional, e.g. Holiday / Maintenance)" value={reason} onChange={(e) => setReason(e.target.value)} className="bg-[#0a0a0a] border-white/15 mb-3 max-w-sm" data-testid="block-reason-input" />
          <div className="glass rounded-2xl p-4 inline-block">
            <Calendar mode="single" selected={selected} onSelect={(d) => d && setSelected(d)}
              modifiers={{ blocked: (d) => fullDays.has(fmtDate(d)), partial: (d) => !fullDays.has(fmtDate(d)) && partialDays.has(fmtDate(d)) }}
              modifiersClassNames={{ blocked: "bg-red-500/30 text-red-300 rounded-md", partial: "bg-amber-500/25 text-amber-200 rounded-md" }}
              className="text-white" />
          </div>
          <div className="mt-6">
            <h2 className="font-display text-lg text-white mb-3">Blocked Days</h2>
            {blocked.length === 0 ? <p className="text-white/40 text-sm">No blocked days.</p> : (
              <div className="space-y-2 max-w-sm">
                {blocked.slice().sort((a, b) => a.date.localeCompare(b.date)).map((b, i) => (
                  <div key={b.id || b.date} className="flex items-center justify-between gap-3 bg-[#111] border border-white/10 rounded-lg px-4 py-2.5" data-testid={`blocked-date-${i}`}>
                    <div className="min-w-0">
                      <p className="text-white text-sm">{b.date}</p>
                      <p className={`text-xs truncate ${(b.service_ids || []).length ? "text-amber-300/80" : "text-red-300/80"}`} data-testid={`blocked-date-services-${i}`}>{nameOf(b.service_ids)}</p>
                      {b.reason && <p className="text-white/40 text-xs">{b.reason}</p>}
                    </div>
                    <button onClick={() => run(() => api.delete(`/availability/${b.date}`, { params: b.id ? { block_id: b.id } : {} }), "Day unblocked")} data-testid={`unblock-date-${i}`} className="text-xs text-red-400 hover:text-white shrink-0">Unblock</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div>
          {!selDate ? (
            <div className="glass rounded-2xl p-8 text-center text-white/40 text-sm flex flex-col items-center gap-3">
              <CalendarDays size={28} className="text-white/20" /> Select a date to manage its time slots.
            </div>
          ) : (
            <div className="glass rounded-2xl p-6" data-testid="slot-manager">
              <div className="flex items-center justify-between gap-3 mb-4">
                <div>
                  <p className="font-mono-label uppercase tracking-[0.2em] text-xs text-[#D4AF37]">{selected.toDateString()}</p>
                  <h2 className="font-display text-xl text-white mt-1 flex items-center gap-2"><Clock size={18} className="text-[#D4AF37]" /> Time Slots</h2>
                </div>
                <button onClick={toggleDay} data-testid="toggle-day-btn"
                  className={`text-xs rounded-full px-4 py-2 flex items-center gap-1.5 ${scopeDayBlock ? "bg-red-500/20 text-red-300" : "border border-white/20 text-white/70 hover:border-red-500/60"}`}>
                  <Ban size={13} /> {scopeDayBlock ? "Unblock Day" : "Block Whole Day"}{scope.length ? " (selected services)" : ""}
                </button>
              </div>
              {dayBlocks.length > 0 && (
                <div className="mb-4 space-y-1" data-testid="day-blocks-list">
                  {dayBlocks.map((b) => (
                    <p key={b.id} className={`text-xs ${(b.service_ids || []).length ? "text-amber-300" : "text-red-300"}`}>Day blocked for: {nameOf(b.service_ids)}{b.reason ? ` — ${b.reason}` : ""}</p>
                  ))}
                </div>
              )}
              {closedWeekday ? (
                <p className="text-red-400 text-sm">Studio is closed on this weekday (see Studio Hours).</p>
              ) : allDayBlocked ? (
                <p className="text-red-400 text-sm">This whole day is blocked for all services.</p>
              ) : (
                <>
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                    {slots.map((h) => {
                      const isBooked = bookedHours.has(h);
                      const hb = slotBlocks.filter((b) => b.hour === h);
                      const full = hb.some((b) => !(b.service_ids || []).length);
                      const mine = hb.some((b) => sameScope(b.service_ids, scope));
                      const label = hb.map((b) => nameOf(b.service_ids)).join(" · ");
                      return (
                        <button key={h} disabled={isBooked} onClick={() => toggleSlot(h)} data-testid={`slot-${h}`}
                          title={isBooked ? "Booked" : hb.length ? `Blocked for: ${label}` : "Available — click to block"}
                          className={`py-2 px-1 rounded-lg text-sm flex flex-col items-center gap-0.5 transition-colors ${
                            isBooked ? "bg-white/5 text-white/30 cursor-not-allowed"
                            : full ? "bg-red-500/25 text-red-300 border border-red-500/40"
                            : hb.length ? `bg-amber-500/15 text-amber-200 border ${mine ? "border-amber-400" : "border-amber-500/30"}`
                            : "border border-white/15 text-white/80 hover:border-[#D4AF37]/60"}`}>
                          {fmtHour(h)}
                          {isBooked && <span className="text-[8px] uppercase">booked</span>}
                          {hb.length > 0 && !isBooked && (
                            <span className="text-[8px] leading-tight text-center line-clamp-2 flex items-center gap-0.5" data-testid={`slot-${h}-scope`}><Lock size={8} className="shrink-0" />{full ? "All" : label}</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                  <div className="flex flex-wrap items-center gap-4 mt-4 text-[11px] text-white/40">
                    <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm border border-white/20" /> Available</span>
                    <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm bg-red-500/25 border border-red-500/40" /> Blocked (all)</span>
                    <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm bg-amber-500/15 border border-amber-500/30" /> Blocked (some services)</span>
                    <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm bg-white/5" /> Booked</span>
                  </div>
                  <p className="text-white/35 text-[11px] mt-2">Tapping a slot adds or removes a block for the services selected above.</p>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
